import React, { useEffect, useState } from 'react';
import { forumApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MessagesSquare, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';

export default function ForumAdminPage() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await forumApi.listCategories(); setCats(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { name: editing.name, description: editing.description || '', icon: editing.icon || 'MessagesSquare', color: editing.color || '#0E7490', order: editing.order || 0 };
      if (editing.id) await forumApi.updateCategory(editing.id, payload);
      else await forumApi.createCategory(payload);
      toast.success('Category saved');
      setEditing(null);
      await load();
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="space-y-6" data-testid="forum-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessagesSquare className="w-6 h-6 text-[#0E7490]" />Community Forum
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Set up categories. Threads are created by users in the forum itself.</p>
        </div>
        <Button className="careiq-btn-primary" onClick={() => setEditing({ name: '', icon: 'MessagesSquare', color: '#0E7490', order: 0 })} data-testid="new-forum-cat-btn">
          <Plus className="w-4 h-4 mr-1" />New Category
        </Button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-[#0E7490]" /> : (
        <div className="space-y-2">
          {cats.map(c => (
            <Card key={c.id} className="careiq-card" data-testid={`forum-cat-row-${c.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.color}20`, color: c.color }}>
                    <MessagesSquare className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0A2342]">{c.name}</div>
                    <div className="text-xs text-[#64748B]">{c.description || '—'} · {c.thread_count} threads · {c.post_count} posts</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`edit-forum-cat-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" className="text-[#991B1B]" onClick={async () => {
                    if (!window.confirm('Delete category and all its threads?')) return;
                    await forumApi.deleteCategory(c.id); toast.success('Deleted'); await load();
                  }} data-testid={`delete-forum-cat-${c.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {cats.length === 0 && <Card className="careiq-card"><CardContent className="p-10 text-center text-[#64748B]">No categories yet.</CardContent></Card>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent data-testid="forum-cat-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit category' : 'New category'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Category name (e.g. General Chat)" data-testid="forum-cat-name-input" />
              <Textarea value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} placeholder="Description" data-testid="forum-cat-desc-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-[#64748B] mb-1">Color</div>
                  <Input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-8" data-testid="forum-cat-color-input" />
                </div>
                <div>
                  <div className="text-xs text-[#64748B] mb-1">Order</div>
                  <Input type="number" value={editing.order || 0} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 0 })} data-testid="forum-cat-order-input" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={save} disabled={!editing?.name?.trim()} data-testid="forum-cat-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
